import type { ResourceBlock } from '@domain/schemas/contract.schema';
import type {
  CreateBlockPayloadInput,
  CreateResourceBlockPayload,
  UpdateBlockPayloadInput,
} from '@domain/schemas/payload.schema';
import type { UpdateResourceBlockPayload } from '@src/mutations/payloads';

import type { CallbackState } from './types';

export const initialCallbackState: CallbackState = { fn: undefined };

type BlockKey = keyof ResourceBlock;

// Each list is a tuple checked against the payload it builds, so `pick` keeps required fields required and a key the
// route does not accept (e.g. `section_id` on a library block) is a type error rather than a silent extra field.
type KeysOf<Payload> = readonly (keyof Payload & BlockKey)[];

export const AS_NEW_BLOCK_PAYLOAD_KEYS = [
  'canDelete',
  'canEdit',
  'canEditOnTemplate',
  'class_of_business',
  'classification',
  'clause_reference',
  'clause_type',
  'content',
  'expiry_date',
  'helperText',
  'inception_date',
  'linkedDatapoints',
  'repeaterIds',
  'shouldInsertPageBreak',
  'teams',
  'title',
  'type',
  'variations',
] as const satisfies KeysOf<CreateBlockPayloadInput>;

export const CREATE_LIBRARY_BLOCK_PAYLOAD_KEYS = [
  'canDelete',
  'canEdit',
  'canEditOnTemplate',
  'class_of_business',
  'classification',
  'clause_reference',
  'clause_type',
  'content',
  'expiry_date',
  'helperText',
  'inception_date',
  'linkedDatapoints',
  'name',
  'repeaterIds',
  'shouldInsertPageBreak',
  'teams',
  'title',
  'type',
  'variations',
] as const satisfies KeysOf<CreateBlockPayloadInput>;

export const CREATE_RESOURCE_BLOCK_PAYLOAD_KEYS = [
  ...CREATE_LIBRARY_BLOCK_PAYLOAD_KEYS,
  'blockLibraryId',
  'canDeleteOnTemplate',
  'section_id',
  'visibility',
] as const satisfies KeysOf<CreateResourceBlockPayload>;

export const UPDATE_TEMPLATE_BLOCK_PERMISSIONS_KEYS = [
  'canDelete',
  'canDeleteOnTemplate',
  'canEdit',
] as const satisfies KeysOf<UpdateResourceBlockPayload['block']>;
export const UPDATE_LIBRARY_BLOCK_PERMISSIONS_KEYS = [
  'canDelete',
  'canEdit',
  'canEditOnTemplate',
] as const satisfies KeysOf<UpdateBlockPayloadInput>;

export const UPDATE_LIBRARY_BLOCK_PAYLOAD_KEYS = [
  ...UPDATE_LIBRARY_BLOCK_PERMISSIONS_KEYS,
  'class_of_business',
  'classification',
  'clause_reference',
  'clause_type',
  'content',
  'helperText',
  'linkedDatapoints',
  'name',
  'repeaterIds',
  'shouldInsertPageBreak',
  'teams',
  'title',
  'type',
  'variations',
  'inception_date',
  'expiry_date',
  'last_imported_at',
] as const satisfies KeysOf<UpdateBlockPayloadInput>;

export const UPDATE_RESOURCE_BLOCK_PAYLOAD_KEYS = [
  ...UPDATE_LIBRARY_BLOCK_PAYLOAD_KEYS,
  'canDeleteOnTemplate',
  'section_id',
  'selectedVariationId',
  'visibility',
] as const satisfies KeysOf<UpdateResourceBlockPayload['block']>;
